// Tekenen van de stad: wegen, gebouwen, grachten, verkeerslichten en straatmeubilair.
// Methoden worden op BezorgerGame.prototype gezet (zie game/mixins.js).

import { t } from '../i18n';
export class RenderWorldMixin {
  render() {
    const ctx = this.ctx;
    // Alles in CSS-pixels; de canvas zelf heeft dpr keer zoveel pixels voor scherpte.
    const w = this.viewW;
    const h = this.viewH;
    const z = this.camera.zoom;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const shakeX = this.shake > 0 ? (Math.random() - 0.5) * this.shake : 0;
    const shakeY = this.shake > 0 ? (Math.random() - 0.5) * this.shake : 0;

    // Zichtbaar deel van de wereld (met marge), voor het overslaan van wat buiten beeld valt.
    const halfW = w / 2 / z;
    const halfH = h / 2 / z;
    this.view = { x0: this.camera.x - halfW, y0: this.camera.y - halfH, x1: this.camera.x + halfW, y1: this.camera.y + halfH };

    ctx.save();
    ctx.translate(w / 2 + shakeX, h / 2 + shakeY);
    ctx.scale(z, z);
    ctx.translate(-this.camera.x, -this.camera.y);

    // 1. Gras, alleen het zichtbare stuk
    ctx.fillStyle = this.isNight ? '#1e2922' : '#7cb342';
    ctx.fillRect(this.view.x0 - 10, this.view.y0 - 10, halfW * 2 + 20, halfH * 2 + 20);

    // Sidewalk border around blocks
    this.renderSidewalks(ctx);

    // 2. Draw Roads & Canals
    this.renderRoads(ctx);
    this.renderCanalsAndBridges(ctx);

    // 2b. Draw Traffic Light Stop Lines on asphalt
    this.renderTrafficLightStopLines(ctx);

    // 2c. Route naar het actieve doel
    this.renderRouteLine(ctx);

    // 3. Draw Skid Marks
    this.renderSkidMarks(ctx);

    // 4. Draw Puddles & Speed Bumps
    this.renderRoadHazards(ctx);

    // 4a. Marktdag: afgesloten straat met kramen
    this.renderClosures(ctx);

    // 4b. Draw Dynamic Weather World Elements (Glossy wet asphalt & volumetric mist in world space)
    this.renderWorldWeather(ctx);

    // 4c. Draw Collectible Coins in the city
    this.renderCoins(ctx);

    // 5. Draw Dynamic Obstacles (Parked van with hazard lights & road works + cones)
    this.renderDynamicObstacles(ctx);

    // 5b. Draw Traffic Cars
    this.renderCars(ctx);

    // 6. Draw Buildings & Delivery Points
    this.renderBuildings(ctx);
    this.renderTargets(ctx);

    // 6b. Draw City Street Decorations (Trees, Benches, Bike Racks, Flower Planters)
    this.renderStreetDecorations(ctx);

    // 6c. Draw Traffic Lights & Speed Cameras (Nederlands achtergrondschild & flitspalen)
    this.renderTrafficLights(ctx);

    // 7. Draw Cyclists (Peloton & Bakfietsen) & Cats
    this.renderCyclists(ctx);
    this.renderCats(ctx);

    // 8. Draw Exhaust & Particles
    this.renderExhaust(ctx);
    this.renderShockParticles(ctx);
    this.renderConfetti(ctx);
    this.renderFloatingTexts(ctx);

    // 9. Draw Scooter
    this.renderScooter(ctx);

    // 9a. De lading op de bak
    this.renderCargo(ctx);

    // 9b. Richtingpijl rond de scooter
    if (this.mode === 'shift') this.renderGuideArrow(ctx);

    ctx.restore();

    // 10. Nacht: donkere laag met lichtcirkels
    if (this.isNight) this.renderNightLayer(ctx, w, h);

    // 11. Screen-Space Atmospheric Weather Overlay (Raindrops, splash droplets, mist haze, thunder flash)
    this.renderWeatherOverlay(ctx, w, h);

    // 12. Screen-Space Politie Flitscamera Flits (bij door rood rijden)
    this.renderCameraFlash(ctx, w, h);

    // 13. Doelen buiten beeld en de flitspaalwaarschuwing
    if (this.mode === 'shift') this.renderEdgeMarkers(ctx, w, h);
    this.renderMinimap(ctx, w, h);
    if (this.mode === 'shift' || this.mode === 'freeride') this.renderFlitsmeisterHUD(ctx, w, h);
  }

  // Witte stopstrepen en Nederlandse STOP-wegmarkeringen op het asfalt
  renderTrafficLightStopLines(ctx) {
    if (!this.map || !this.map.intersections) return;
    ctx.save();
    for (const inter of this.map.intersections) {
      if (!this.inView(inter.x, inter.y, 200)) continue; // buiten beeld: overslaan
      if (!inter.hasTrafficLight) continue;

      ctx.fillStyle = '#ffffff';
      // 1. West-aanrijroute (Oostwaarts verkeer, zuidelijke weghelft)
      ctx.fillRect(inter.x - inter.hw - 5, inter.y, 6, inter.hh);
      // 2. Oost-aanrijroute (Westwaarts verkeer, noordelijke weghelft)
      ctx.fillRect(inter.x + inter.hw - 1, inter.y - inter.hh, 6, inter.hh);
      // 3. Noord-aanrijroute (Zuidwaarts verkeer, westelijke weghelft)
      ctx.fillRect(inter.x - inter.hw, inter.y - inter.hh - 5, inter.hw, 6);
      // 4. Zuid-aanrijroute (Noordwaarts verkeer, oostelijke weghelft)
      ctx.fillRect(inter.x, inter.y + inter.hh - 1, inter.hw, 6);

      // Witte STOP markeringen op het asfalt voor de strepen
      ctx.fillStyle = 'rgba(255, 255, 255, 0.42)';
      ctx.font = 'bold 9px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('STOP', inter.x - inter.hw - 22, inter.y + inter.hh * 0.5);
      ctx.fillText('STOP', inter.x + inter.hw + 22, inter.y - inter.hh * 0.5);
      ctx.fillText('STOP', inter.x - inter.hw * 0.5, inter.y - inter.hh - 22);
      ctx.fillText('STOP', inter.x + inter.hw * 0.5, inter.y + inter.hh + 22);
    }
    ctx.restore();
  }

  // Verkeerslichten (Nederlands achtergrondschild met witte contrastrand) en Flitspalen
  renderTrafficLights(ctx) {
    if (!this.map || !this.map.intersections) return;
    ctx.save();

    for (const inter of this.map.intersections) {
      if (!this.inView(inter.x, inter.y, 200)) continue; // buiten beeld: overslaan
      if (!inter.hasTrafficLight || !inter.trafficLight) continue;

      const tl = inter.trafficLight;
      const hState = tl.horizontal; // 'green', 'orange', 'red'
      const vState = tl.vertical;   // 'green', 'orange', 'red'

      // 4 Verkeerslicht-masten per kruising aan de rechterzijde van de naderende rijbaan:
      const signalPoles = [
        // 1. West-aanrijroute (oostwaarts kijkend, zuidzijde)
        { x: inter.x - inter.hw - 14, y: inter.y + inter.hh + 12, state: hState },
        // 2. Oost-aanrijroute (westwaarts kijkend, noordzijde)
        { x: inter.x + inter.hw + 14, y: inter.y - inter.hh - 12, state: hState },
        // 3. Noord-aanrijroute (zuidwaarts kijkend, westzijde)
        { x: inter.x - inter.hw - 12, y: inter.y - inter.hh - 14, state: vState },
        // 4. Zuid-aanrijroute (noordwaarts kijkend, oostzijde)
        { x: inter.x + inter.hw + 12, y: inter.y + inter.hh + 14, state: vState }
      ];

      for (const sp of signalPoles) {
        // Schaduw van paal en armatuur
        ctx.fillStyle = 'rgba(0, 0, 0, 0.28)';
        ctx.beginPath();
        ctx.ellipse(sp.x + 3, sp.y + 4, 8, 4, 0, 0, Math.PI * 2);
        ctx.fill();

        // Stalen mastvoet
        ctx.fillStyle = '#64748b';
        ctx.beginPath();
        ctx.arc(sp.x, sp.y, 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#94a3b8';
        ctx.beginPath();
        ctx.arc(sp.x, sp.y, 2, 0, Math.PI * 2);
        ctx.fill();

        // Nederlands Achtergrondschild (zwart met witte contrastrand)
        const boxW = 14;
        const boxH = 34;
        const boxX = sp.x - boxW / 2;
        const boxY = sp.y - boxH + 6;

        // Gloed op het wegdek wanneer actief
        let activeLampY = boxY + 6;
        let glowColor = null;
        if (sp.state === 'red') {
          activeLampY = boxY + 6;
          glowColor = 'rgba(239, 68, 68, ';
        } else if (sp.state === 'orange') {
          activeLampY = boxY + 17;
          glowColor = 'rgba(245, 158, 11, ';
        } else if (sp.state === 'green') {
          activeLampY = boxY + 28;
          glowColor = 'rgba(16, 185, 129, ';
        }

        if (glowColor) {
          const glowRad = this.isNight ? 34 : 18;
          const glowGrad = ctx.createRadialGradient(sp.x, activeLampY, 2, sp.x, activeLampY, glowRad);
          glowGrad.addColorStop(0, `${glowColor}0.75)`);
          glowGrad.addColorStop(0.5, `${glowColor}0.25)`);
          glowGrad.addColorStop(1, `${glowColor}0)`);
          ctx.fillStyle = glowGrad;
          ctx.beginPath();
          ctx.arc(sp.x, activeLampY, glowRad, 0, Math.PI * 2);
          ctx.fill();
        }

        // Zwart achtergrondschild met afgeronde hoeken
        ctx.fillStyle = '#09090b';
        ctx.beginPath();
        ctx.roundRect(boxX, boxY, boxW, boxH, 4);
        ctx.fill();

        // Witte Nederlandse bies (contrastrand)
        ctx.strokeStyle = '#f8fafc';
        ctx.lineWidth = 1.2;
        ctx.stroke();

        // 3 Lampen: Rood (boven), Oranje (midden), Groen (onder)
        const lampR = 3.6;

        // Rood
        ctx.fillStyle = (sp.state === 'red') ? '#ef4444' : '#3f1717';
        ctx.beginPath();
        ctx.arc(sp.x, boxY + 6, lampR, 0, Math.PI * 2);
        ctx.fill();
        if (sp.state === 'red') {
          ctx.fillStyle = '#fee2e2';
          ctx.beginPath();
          ctx.arc(sp.x - 1, boxY + 5, 1.2, 0, Math.PI * 2);
          ctx.fill();
        }

        // Oranje (Amber)
        ctx.fillStyle = (sp.state === 'orange') ? '#f59e0b' : '#3b2507';
        ctx.beginPath();
        ctx.arc(sp.x, boxY + 17, lampR, 0, Math.PI * 2);
        ctx.fill();
        if (sp.state === 'orange') {
          ctx.fillStyle = '#fef3c7';
          ctx.beginPath();
          ctx.arc(sp.x - 1, boxY + 16, 1.2, 0, Math.PI * 2);
          ctx.fill();
        }

        // Groen
        ctx.fillStyle = (sp.state === 'green') ? '#10b981' : '#0a2e1d';
        ctx.beginPath();
        ctx.arc(sp.x, boxY + 28, lampR, 0, Math.PI * 2);
        ctx.fill();
        if (sp.state === 'green') {
          ctx.fillStyle = '#d1fae5';
          ctx.beginPath();
          ctx.arc(sp.x - 1, boxY + 27, 1.2, 0, Math.PI * 2);
          ctx.fill();
        }

        // Zonnekappen (kapjes boven de lampen)
        ctx.strokeStyle = '#27272a';
        ctx.lineWidth = 1.2;
        [boxY + 3, boxY + 14, boxY + 25].forEach(ky => {
          ctx.beginPath();
          ctx.arc(sp.x, ky + 2, 4.5, Math.PI, Math.PI * 2);
          ctx.stroke();
        });
      }

      // Flitspaal (Politie Verkeerscamera op geselecteerde strategische kruispunten)
      if (tl.hasSpeedCamera) {
        const camX = inter.x - inter.hw - 18;
        const camY = inter.y - inter.hh - 18;

        // Paalschaduw
        ctx.fillStyle = 'rgba(0, 0, 0, 0.25)';
        ctx.beginPath();
        ctx.ellipse(camX + 3, camY + 3, 7, 4, 0, 0, Math.PI * 2);
        ctx.fill();

        // Geel/donkergrijs gestreepte mast (Nederlandse flitspaal)
        const poleH = 22;
        ctx.lineWidth = 4;
        for (let py = 0; py < poleH; py += 5) {
          ctx.strokeStyle = (Math.floor(py / 5) % 2 === 0) ? '#eab308' : '#334155';
          ctx.beginPath();
          ctx.moveTo(camX, camY - py);
          ctx.lineTo(camX, camY - Math.min(poleH, py + 5));
          ctx.stroke();
        }

        // Camerakast (behuizing)
        const cBoxW = 16;
        const cBoxH = 14;
        ctx.fillStyle = '#1e293b';
        ctx.beginPath();
        ctx.roundRect(camX - cBoxW / 2, camY - poleH - cBoxH, cBoxW, cBoxH, 3);
        ctx.fill();
        ctx.strokeStyle = '#eab308';
        ctx.lineWidth = 1.2;
        ctx.stroke();

        // Cameralens & Flitser reflectoren
        ctx.fillStyle = '#020617';
        ctx.beginPath();
        ctx.arc(camX - 3.5, camY - poleH - cBoxH / 2, 3, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#60a5fa';
        ctx.beginPath();
        ctx.arc(camX - 3.5, camY - poleH - cBoxH / 2, 1.2, 0, Math.PI * 2);
        ctx.fill();

        // Flitslens (wit/zilver)
        ctx.fillStyle = '#f8fafc';
        ctx.beginPath();
        ctx.rect(camX + 1.5, camY - poleH - cBoxH / 2 - 2.5, 4.5, 5);
        ctx.fill();

        // Subtiel "📸 FLITSPAAL" labeltje bij nabijheid van de scooter
        const distToScooter = Math.hypot(this.scooter.x - camX, this.scooter.y - camY);
        if (distToScooter < 140) {
          ctx.fillStyle = 'rgba(15, 23, 42, 0.85)';
          ctx.beginPath();
          ctx.roundRect(camX - 28, camY - poleH - cBoxH - 15, 56, 12, 4);
          ctx.fill();
          ctx.strokeStyle = 'rgba(234, 179, 8, 0.8)';
          ctx.lineWidth = 1;
          ctx.stroke();

          ctx.fillStyle = '#fef08a';
          ctx.font = 'bold 8px sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(t('world.speedCameraPole'), camX, camY - poleH - cBoxH - 9);
        }
      }
    }

    ctx.restore();
  }

  // Witte cameraflits over het hele scherm wanneer de speler door rood rijdt
  renderCameraFlash(ctx, w, h) {
    if (this.cameraFlashTimer > 0) {
      ctx.save();
      const alpha = Math.min(this.reduceMotion ? 0.15 : 0.9, this.cameraFlashTimer * 2.2);
      ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
      ctx.fillRect(0, 0, w, h);
      ctx.restore();
    }
  }

  renderSidewalks(ctx) {
    // Subtle sidewalk grid pattern
    ctx.fillStyle = this.isNight ? '#334155' : '#e2e8f0';
    for (const b of this.map.buildings) {
      if (!this.inView(b.x + b.w / 2, b.y + b.h / 2, Math.max(b.w, b.h))) continue; // buiten beeld: overslaan
      ctx.fillRect(b.x - 24, b.y - 24, b.w + 48, b.h + 48);
    }
    // Bakery sidewalk
    const bk = this.map.bakery;
    ctx.fillRect(bk.x - 30, bk.y - 30, bk.w + 60, bk.h + 60);
  }

  renderRoads(ctx) {
    for (const r of this.map.roads) {
      if (r.klinkers) {
        // Klinkerstraat: warm reddish brick pattern
        ctx.fillStyle = this.isNight ? '#452b21' : '#b45309';
        ctx.fillRect(r.x, r.y, r.w, r.h);

        // Brick lines
        ctx.strokeStyle = this.isNight ? '#2e1c15' : '#92400e';
        ctx.lineWidth = 1.5;
        for (let y = r.y; y < r.y + r.h; y += 18) {
          ctx.beginPath();
          ctx.moveTo(r.x, y);
          ctx.lineTo(r.x + r.w, y);
          ctx.stroke();
        }
      } else {
        // Dark Asphalt
        ctx.fillStyle = this.isNight ? '#0f172a' : '#27272a';
        ctx.fillRect(r.x, r.y, r.w, r.h);

        // White Lane Divider lines
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 4;
        ctx.setLineDash([24, 20]);

        if (r.w > r.h) {
          // Horizontal road divider
          ctx.beginPath();
          ctx.moveTo(r.x, r.y + r.h / 2);
          ctx.lineTo(r.x + r.w, r.y + r.h / 2);
          ctx.stroke();
        } else {
          // Vertical road divider
          ctx.beginPath();
          ctx.moveTo(r.x + r.w / 2, r.y);
          ctx.lineTo(r.x + r.w / 2, r.y + r.h);
          ctx.stroke();
        }
        ctx.setLineDash([]); // Reset dash

        // Pedestrian Zebra Crossings near intersections
        this.renderZebraCrossings(ctx, r);

        // Curbside Loading & Delivery zones for Knus Expres & player
        this.renderCurbsideLoadingZones(ctx, r);
      }
    }
  }

  renderCurbsideLoadingZones(ctx, r) {
    if (!r) return;
    ctx.save();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.45)';
    ctx.lineWidth = 1.8;
    ctx.setLineDash([6, 6]);

    // Curbside bays along Hoofdstraat (y = 1104 north curb or y = 1254 south curb)
    if (r.name === 'Hoofdstraat') {
      const bays = [
        { x: 1600, y: 1104, w: 100, h: 42 }, // Grand Café Het Anker
        { x: 1430, y: 1254, w: 100, h: 42 }, // Tandartspraktijk Wit
        { x: 1690, y: 1254, w: 100, h: 42 }, // Atelier Bloem
        { x: 2100, y: 1254, w: 100, h: 42 }, // Notaris Oost
        { x: 890,  y: 1254, w: 100, h: 42 }, // Meneer van Dijk
        { x: 600,  y: 1254, w: 100, h: 42 }  // Knus Expres Depot
      ];
      for (const b of bays) {
        ctx.strokeRect(b.x, b.y, b.w, b.h);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.35)';
        ctx.font = 'bold 9px "Plus Jakarta Sans", sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(t('world.loadingZone'), b.x + b.w / 2, b.y + b.h / 2 + 3);
      }
    } else if (r.name === 'Noorderlaan') {
      const bays = [
        { x: 590,  y: 354, w: 100, h: 42 }, // Familie Jansen
        { x: 880,  y: 354, w: 100, h: 42 }, // Kantoor Horizon
        { x: 1430, y: 354, w: 100, h: 42 }  // Villa Rozenhof
      ];
      for (const b of bays) {
        ctx.strokeRect(b.x, b.y, b.w, b.h);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.35)';
        ctx.font = 'bold 9px "Plus Jakarta Sans", sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(t('world.loadingZone'), b.x + b.w / 2, b.y + b.h / 2 + 3);
      }
    } else if (r.name === 'Klinkerstraat') {
      // Along Studentenflat Campus (x = 354)
      const b = { x: 354, y: 1370, w: 42, h: 100 };
      ctx.strokeRect(b.x, b.y, b.w, b.h);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.35)';
      ctx.font = 'bold 9px "Plus Jakarta Sans", sans-serif';
      ctx.textAlign = 'center';
      ctx.save();
      ctx.translate(b.x + b.w / 2, b.y + b.h / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.fillText(t('world.loadingZone'), 0, 3);
      ctx.restore();
    }
    ctx.restore();
  }

  renderZebraCrossings(ctx, r) {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
    if (r.name === 'Hoofdstraat') {
      // Near intersection with Bakkerijboulevard
      const zebraX = 1040;
      for (let y = r.y + 10; y < r.y + r.h - 10; y += 22) {
        ctx.fillRect(zebraX, y, 40, 12);
      }
      const zebraX2 = 1320;
      for (let y = r.y + 10; y < r.y + r.h - 10; y += 22) {
        ctx.fillRect(zebraX2, y, 40, 12);
      }
    }
  }

  renderSkidMarks(ctx) {
    if (this.scooter.skidMarks.length < 2) return;
    ctx.save();
    for (let i = 1; i < this.scooter.skidMarks.length; i++) {
      const p1 = this.scooter.skidMarks[i - 1];
      const p2 = this.scooter.skidMarks[i];
      ctx.strokeStyle = `rgba(10, 10, 10, ${p2.alpha})`;
      ctx.lineWidth = 6;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.stroke();
    }
    ctx.restore();
  }

  renderRoadHazards(ctx) {
    // Water Puddles
    for (const p of this.map.puddles) {
      if (!this.inView(p.x, p.y, 80)) continue; // buiten beeld: overslaan
      ctx.save();
      const grad = ctx.createRadialGradient(p.x, p.y, 2, p.x, p.y, p.r);
      grad.addColorStop(0, 'rgba(56, 189, 248, 0.55)');
      grad.addColorStop(0.8, 'rgba(14, 116, 144, 0.45)');
      grad.addColorStop(1, 'rgba(14, 116, 144, 0)');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    // Speed Bumps (Drempels with diagonal hazard stripes)
    for (const b of this.map.speedBumps) {
      if (!this.inView(b.x + b.w / 2, b.y + b.h / 2, 260)) continue; // buiten beeld: overslaan
      ctx.save();
      ctx.fillStyle = '#facc15'; // Yellow
      ctx.fillRect(b.x, b.y, b.w, b.h);

      // Black stripes
      ctx.fillStyle = '#000000';
      if (b.orientation === 'vertical') {
        for (let y = b.y; y < b.y + b.h; y += 24) {
          ctx.fillRect(b.x, y, b.w, 12);
        }
      } else {
        for (let x = b.x; x < b.x + b.w; x += 24) {
          ctx.fillRect(x, b.y, 12, b.h);
        }
      }
      ctx.restore();
    }
  }

  renderBuildings(ctx) {
    // 1. Bakery HQ: Luxe Historische Bakkerij met gevelversiering, luifel & dampende schoorsteen
    const bk = this.map.bakery;
    ctx.save();
    // Gevelschaduw
    ctx.fillStyle = 'rgba(0, 0, 0, 0.22)';
    ctx.fillRect(bk.x + 10, bk.y + 10, bk.w, bk.h);

    // Metselwerk bakstenen basis
    ctx.fillStyle = '#b45309'; // Warm ambachtelijk baksteen
    ctx.fillRect(bk.x, bk.y, bk.w, bk.h);

    // Voegwerk / baksteenlijnen (subtiel)
    ctx.strokeStyle = 'rgba(254, 243, 199, 0.15)';
    ctx.lineWidth = 1;
    for (let by = bk.y + 12; by < bk.y + bk.h; by += 16) {
      ctx.beginPath();
      ctx.moveTo(bk.x, by);
      ctx.lineTo(bk.x + bk.w, by);
      ctx.stroke();
    }

    // Monumentaal Hollands pannendak
    ctx.fillStyle = '#78350f';
    ctx.fillRect(bk.x + 12, bk.y + 12, bk.w - 24, bk.h - 24);

    // Schoorsteen met rookwolkjes
    const chimX = bk.x + 30;
    const chimY = bk.y + 24;
    ctx.fillStyle = '#9a3412';
    ctx.fillRect(chimX - 6, chimY - 6, 12, 12);
    ctx.strokeStyle = '#451a03';
    ctx.lineWidth = 1.2;
    ctx.strokeRect(chimX - 6, chimY - 6, 12, 12);

    // Ramen van de bakkerij met warme gloed
    const winCols = 4;
    const winSpacing = (bk.w - 40) / winCols;
    for (let c = 0; c < winCols; c++) {
      const wx = bk.x + 22 + c * winSpacing;
      const wy = bk.y + 35;
      ctx.fillStyle = this.isNight ? '#fef08a' : '#e0f2fe';
      ctx.fillRect(wx, wy, 16, 20);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.2;
      ctx.strokeRect(wx, wy, 16, 20);
      // Ruitverdeling (kruiskozijn)
      ctx.beginPath();
      ctx.moveTo(wx + 8, wy);
      ctx.lineTo(wx + 8, wy + 20);
      ctx.moveTo(wx, wy + 10);
      ctx.lineTo(wx + 16, wy + 10);
      ctx.stroke();
    }

    // Authentieke Oranje-Witte Bakkersluifel
    const awningY = bk.y + bk.h - 18;
    const awningW = bk.w - 30;
    const awningX = bk.x + 15;
    const stripeCount = 14;
    const stripeW = awningW / stripeCount;
    for (let s = 0; s < stripeCount; s++) {
      ctx.fillStyle = (s % 2 === 0) ? '#f97316' : '#ffffff';
      ctx.fillRect(awningX + s * stripeW, awningY, stripeW, 14);
    }
    ctx.strokeStyle = '#c2410c';
    ctx.lineWidth = 1;
    ctx.strokeRect(awningX, awningY, awningW, 14);

    // Bakkers Uithangbord
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.roundRect(bk.x + bk.w / 2 - 95, bk.y + bk.h / 2 - 20, 190, 36, 6);
    ctx.fill();
    ctx.strokeStyle = '#d97706';
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.fillStyle = '#78350f';
    ctx.font = 'bold 14px "Plus Jakarta Sans", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('🥖 KNUS & KROKANT', bk.x + bk.w / 2, bk.y + bk.h / 2 - 4);
    ctx.font = '10px "Plus Jakarta Sans", sans-serif';
    ctx.fillStyle = '#b45309';
    ctx.fillText(t('world.bakeryTagline'), bk.x + bk.w / 2, bk.y + bk.h / 2 + 10);

    // Laadzone markering
    const lz = bk.loadingZone;
    ctx.fillStyle = 'rgba(245, 158, 11, 0.22)';
    ctx.strokeStyle = '#d97706';
    ctx.lineWidth = 2.5;
    ctx.setLineDash([6, 6]);
    ctx.strokeRect(lz.x, lz.y, lz.w, lz.h);
    ctx.fillRect(lz.x, lz.y, lz.w, lz.h);
    ctx.setLineDash([]);
    ctx.font = 'bold 10px "Plus Jakarta Sans", sans-serif';
    ctx.fillStyle = '#b45309';
    ctx.fillText(t('world.loadingZone'), lz.x + lz.w / 2, lz.y + lz.h / 2);
    ctx.restore();

    // 2. Realistische Nederlandse Grachtenpanden, Herenhuizen & Gebouwen
    for (const b of this.map.buildings) {
      if (!this.inView(b.x + b.w / 2, b.y + b.h / 2, Math.max(b.w, b.h) + 80)) continue; // buiten beeld: overslaan
      ctx.save();
      // Diepe zachte gevelschaduw
      ctx.fillStyle = 'rgba(0, 0, 0, 0.18)';
      ctx.fillRect(b.x + 8, b.y + 8, b.w, b.h);

      // A. Bakstenen Gevelbasis (Authentieke Nederlandse Baksteentinten)
      const brick = b.brickColor || b.color || '#9a3412';
      ctx.fillStyle = brick;
      ctx.fillRect(b.x, b.y, b.w, b.h);

      // Subtiel horizontaal metselwerk reliëf
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
      ctx.lineWidth = 1;
      for (let yL = b.y + 14; yL < b.y + b.h; yL += 18) {
        ctx.beginPath();
        ctx.moveTo(b.x, yL);
        ctx.lineTo(b.x + b.w, yL);
        ctx.stroke();
      }

      // Witte / crèmekleurige natuurstenen kroonlijst / gevelbalk
      ctx.fillStyle = b.trimColor || '#ffffff';
      ctx.fillRect(b.x, b.y, b.w, 6);

      // B. Hollandse Geveltop / Dak (Trapgevel, Klokgevel, Tuitgevel of Herenhuis)
      const roofMargin = 12;
      ctx.fillStyle = b.roof || '#334155';
      ctx.fillRect(b.x + roofMargin, b.y + roofMargin, b.w - roofMargin * 2, b.h - roofMargin * 2);

      // C. Karakteristieke Gevelaccenten aan de straatzijde
      const gable = b.gable || 'herenhuis';
      if (gable === 'trapgevel') {
        // Trapgevel treden
        ctx.fillStyle = b.trimColor || '#ffffff';
        const stepW = 14;
        const stepH = 5;
        for (let st = 0; st < 3; st++) {
          ctx.fillRect(b.x + 10 + st * stepW, b.y - 2 - st * stepH, stepW, stepH);
          ctx.fillRect(b.x + b.w - 10 - (st + 1) * stepW, b.y - 2 - st * stepH, stepW, stepH);
        }
      } else if (gable === 'klokgevel') {
        // Klokgevel sierkrul
        ctx.fillStyle = b.trimColor || '#ffffff';
        ctx.beginPath();
        ctx.arc(b.x + 16, b.y + 2, 7, 0, Math.PI * 2);
        ctx.arc(b.x + b.w - 16, b.y + 2, 7, 0, Math.PI * 2);
        ctx.fill();
      }

      // D. Authentieke Nederlandse Schuiframen (Multi-pane ramen met kruiskozijnen)
      const cols = b.windowCols || 3;
      const rows = b.windowRows || 2;
      const xGap = (b.w - 36) / cols;
      const yGap = (b.h - 48) / rows;

      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const wx = b.x + 18 + c * xGap;
          const wy = b.y + 22 + r * yGap;

          // Raamopening met houten kozijn
          ctx.fillStyle = this.isNight ? '#fef08a' : '#dbeafe';
          ctx.fillRect(wx, wy, 14, 16);
          ctx.strokeStyle = b.trimColor || '#ffffff';
          ctx.lineWidth = 1.2;
          ctx.strokeRect(wx, wy, 14, 16);

          // Roeden / kruisverdeling
          ctx.beginPath();
          ctx.moveTo(wx + 7, wy);
          ctx.lineTo(wx + 7, wy + 16);
          ctx.moveTo(wx, wy + 8);
          ctx.lineTo(wx + 14, wy + 8);
          ctx.stroke();

          // E. Kleurrijke Bloembakken onder de ramen (Geraniums / Tulpen)
          if (b.flowerBoxes && r === rows - 1) {
            ctx.fillStyle = '#78350f'; // Houten bloembak
            ctx.fillRect(wx - 2, wy + 16, 18, 4);
            // Bloemetjes
            const flowerColors = ['#ef4444', '#ec4899', '#f59e0b', '#3b82f6'];
            const flCol = flowerColors[(c + r) % flowerColors.length];
            ctx.fillStyle = flCol;
            ctx.beginPath();
            ctx.arc(wx + 2, wy + 16, 2, 0, Math.PI * 2);
            ctx.arc(wx + 7, wy + 16, 2, 0, Math.PI * 2);
            ctx.arc(wx + 12, wy + 16, 2, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }

      // F. Luifels voor winkels en cafés op de begane grond
      if (b.awning) {
        const awnColors = (b.awning === 'orange_white') ? ['#ea580c', '#ffffff'] :
                          (b.awning === 'green_white') ? ['#16a34a', '#ffffff'] : ['#0284c7', '#ffffff'];
        const numS = 10;
        const aW = Math.min(b.w - 20, 140);
        const aX = b.x + (b.w - aW) / 2;
        const aY = (b.streetSide === 'north') ? b.y : (b.y + b.h - 12);
        const sWidth = aW / numS;
        for (let s = 0; s < numS; s++) {
          ctx.fillStyle = (s % 2 === 0) ? awnColors[0] : awnColors[1];
          ctx.fillRect(aX + s * sWidth, aY, sWidth, 12);
        }
        ctx.strokeStyle = 'rgba(0,0,0,0.2)';
        ctx.lineWidth = 1;
        ctx.strokeRect(aX, aY, aW, 12);
      }

      // G. Voordeur / Hardstenen Stoep aan de straatzijde
      if (b.doorX && b.doorY) {
        if (b.streetSide === 'south') {
          // Hardstenen bordestrap
          ctx.fillStyle = '#64748b';
          ctx.fillRect(b.doorX - 14, b.y + b.h - 6, 28, 6);
          // Donkergroene / klassieke houten dubbele voordeur
          ctx.fillStyle = '#0f172a';
          ctx.fillRect(b.doorX - 10, b.y + b.h - 14, 20, 12);
          // Messing deurknop
          ctx.fillStyle = '#fbbf24';
          ctx.fillRect(b.doorX - 2, b.y + b.h - 8, 3, 3);
        } else if (b.streetSide === 'north') {
          ctx.fillStyle = '#64748b';
          ctx.fillRect(b.doorX - 14, b.y, 28, 6);
          ctx.fillStyle = '#0f172a';
          ctx.fillRect(b.doorX - 10, b.y + 2, 20, 12);
          ctx.fillStyle = '#fbbf24';
          ctx.fillRect(b.doorX - 2, b.y + 6, 3, 3);
        } else if (b.streetSide === 'east') {
          ctx.fillStyle = '#64748b';
          ctx.fillRect(b.x + b.w - 6, b.doorY - 14, 6, 28);
          ctx.fillStyle = '#0f172a';
          ctx.fillRect(b.x + b.w - 14, b.doorY - 10, 12, 20);
          ctx.fillStyle = '#fbbf24';
          ctx.fillRect(b.x + b.w - 8, b.doorY - 2, 3, 3);
        }
      }

      // H. Chique Gevelbordje / Naamlabel
      ctx.fillStyle = 'rgba(15, 23, 42, 0.82)';
      ctx.beginPath();
      const labelW = Math.min(b.w - 24, 180);
      ctx.roundRect(b.x + (b.w - labelW) / 2, b.y + b.h / 2 - 10, labelW, 20, 4);
      ctx.fill();
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
      ctx.lineWidth = 1;
      ctx.stroke();

      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 11px "Plus Jakarta Sans", sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(b.label, b.x + b.w / 2, b.y + b.h / 2);

      ctx.restore();
    }
  }

  // Nederlandse Grachten & Hollandse Ophaalbrug "De Eendracht"
  renderCanalsAndBridges(ctx) {
    if (!this.map) return;

    // 1. Grachtenwater, Kademuren & Rondvaartboot
    if (this.map.canals) {
      for (const canal of this.map.canals) {
        ctx.save();
        // A. Diep waterverloop (Turquoise / Diepblauw)
        const waterGrad = ctx.createLinearGradient(canal.x, 0, canal.x + canal.w, 0);
        waterGrad.addColorStop(0, '#0e7490');
        waterGrad.addColorStop(0.5, '#0891b2');
        waterGrad.addColorStop(1, '#0e7490');
        ctx.fillStyle = waterGrad;
        ctx.fillRect(canal.x, canal.y, canal.w, canal.h);

        // B. Natuurstenen Kademuren (witte / grijze stenen boord)
        ctx.fillStyle = '#94a3b8';
        ctx.fillRect(canal.x - 8, canal.y, 8, canal.h); // Westkade
        ctx.fillRect(canal.x + canal.w, canal.y, 8, canal.h); // Oostkade

        // Kade voegen & bolders (meerpalen)
        ctx.strokeStyle = '#475569';
        ctx.lineWidth = 1.2;
        ctx.strokeRect(canal.x - 8, canal.y, 8, canal.h);
        ctx.strokeRect(canal.x + canal.w, canal.y, 8, canal.h);

        // Zwarte gietijzeren bolders om de 140px
        for (let my = canal.y + 60; my < canal.y + canal.h; my += 140) {
          ctx.fillStyle = '#0f172a';
          ctx.beginPath();
          ctx.arc(canal.x - 4, my, 3.5, 0, Math.PI * 2);
          ctx.arc(canal.x + canal.w + 4, my, 3.5, 0, Math.PI * 2);
          ctx.fill();
        }

        // C. Subtiele kabbelende golfjes
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.28)';
        ctx.lineWidth = 1.2;
        const now = Date.now() * 0.002;
        for (let wy = canal.y + 40; wy < canal.y + canal.h; wy += 85) {
          const waveOff = Math.sin(now + wy * 0.05) * 6;
          ctx.beginPath();
          ctx.moveTo(canal.x + 20 + waveOff, wy);
          ctx.quadraticCurveTo(canal.x + 60 + waveOff, wy - 4, canal.x + 100 + waveOff, wy);
          ctx.stroke();
        }

        // D. Scheepvaart op de Prinsengracht (Vrachtschip & Rondvaartboot)
        const boats = canal.boats || (canal.boat ? [canal.boat] : []);
        for (const b of boats) {
          // Hekgolf deeltjes
          if (b.wakeParticles) {
            for (const wp of b.wakeParticles) {
              ctx.fillStyle = `rgba(255, 255, 255, ${wp.alpha})`;
              ctx.beginPath();
              ctx.arc(wp.x, wp.y, wp.size, 0, Math.PI * 2);
              ctx.fill();
            }
          }

          ctx.save();
          ctx.translate(b.x, b.y);
          ctx.rotate(b.angle);

          // Bootromp schaduw
          ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
          ctx.beginPath();
          ctx.ellipse(3, 4, b.h / 2, b.w / 2, 0, 0, Math.PI * 2);
          ctx.fill();

          if (b.type === 'freighter') {
            // --- Authentiek Hollands Binnenvaartschip "De Volharding" ---
            // 1. Zware Stalen Scheepsromp (Antraciet / Donker marineblauw)
            ctx.fillStyle = b.hullColor || '#1e293b';
            ctx.beginPath();
            ctx.roundRect(-b.h / 2, -b.w / 2, b.h, b.w, 8);
            ctx.fill();
            ctx.strokeStyle = '#475569';
            ctx.lineWidth = 2.5;
            ctx.stroke();

            // 2. Schippersdek (Teakhout / Donkergrijs)
            ctx.fillStyle = b.deckColor || '#334155';
            ctx.fillRect(-b.h / 2 + 10, -b.w / 2 + 4, b.h - 20, b.w - 8);

            // 3. Groot Vrachtruim met Meel- & Graanzakken
            ctx.fillStyle = '#0f172a';
            ctx.fillRect(-b.h / 2 + 32, -b.w / 2 + 7, b.h - 76, b.w - 14);
            ctx.strokeStyle = '#ca8a04';
            ctx.lineWidth = 1.5;
            ctx.strokeRect(-b.h / 2 + 32, -b.w / 2 + 7, b.h - 76, b.w - 14);

            // Graanluiken & Meellading (Knus & Krokant Graanvoorraad)
            const numHatches = 3;
            const hW = (b.h - 82) / numHatches;
            for (let hi = 0; hi < numHatches; hi++) {
              ctx.fillStyle = (hi % 2 === 0) ? '#d97706' : '#b45309';
              ctx.fillRect(-b.h / 2 + 35 + hi * hW, -b.w / 2 + 9, hW - 4, b.w - 18);
              ctx.fillStyle = '#fef3c7';
              ctx.font = 'bold 7px sans-serif';
              ctx.textAlign = 'center';
              ctx.fillText(t('world.grain'), -b.h / 2 + 35 + hi * hW + (hW - 4) / 2, 2);
            }

            // 4. Stuurhut / Kajuit op het achterschip
            ctx.fillStyle = b.cabinColor || '#f8fafc';
            ctx.beginPath();
            ctx.roundRect(-b.h / 2 + 6, -b.w / 2 + 6, 22, b.w - 12, 4);
            ctx.fill();
            ctx.strokeStyle = '#0284c7';
            ctx.lineWidth = 1.2;
            ctx.stroke();

            // Ramen stuurhut
            ctx.fillStyle = '#38bdf8';
            ctx.fillRect(-b.h / 2 + 8, -b.w / 2 + 8, 5, b.w - 16);

            // Scheepsschoorsteen
            ctx.fillStyle = '#dc2626';
            ctx.beginPath();
            ctx.arc(-b.h / 2 + 16, 0, 3, 0, Math.PI * 2);
            ctx.fill();

            // 5. Nederlandse Vlag op de achtersteven
            ctx.fillStyle = '#ef4444'; ctx.fillRect(-b.h / 2 + 2, -3, 8, 2);
            ctx.fillStyle = '#ffffff'; ctx.fillRect(-b.h / 2 + 2, -1, 8, 2);
            ctx.fillStyle = '#2563eb'; ctx.fillRect(-b.h / 2 + 2, 1, 8, 2);

            // Scheepsnaam op de boeg
            ctx.fillStyle = '#ffffff';
            ctx.font = 'bold 8px "Plus Jakarta Sans", sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('DE VOLHARDING', b.h / 2 - 28, 3);
          } else {
            // --- Klassieke Rondvaartboot "De Krokante Zwaan" ---
            // Witte scheepsromp
            ctx.fillStyle = b.color || '#ffffff';
            ctx.beginPath();
            ctx.roundRect(-b.h / 2, -b.w / 2, b.h, b.w, 12);
            ctx.fill();
            ctx.strokeStyle = '#0284c7';
            ctx.lineWidth = 2;
            ctx.stroke();

            // Glazen panoramadak
            ctx.fillStyle = '#38bdf8';
            ctx.beginPath();
            ctx.roundRect(-b.h / 2 + 14, -b.w / 2 + 6, b.h - 28, b.w - 12, 6);
            ctx.fill();

            // Blauwe kajuitdaklijn
            ctx.fillStyle = b.roofColor || '#1e3a8a';
            ctx.fillRect(-b.h / 2 + 24, -4, b.h - 48, 8);

            // Nederlandse Vlag op het achterdek
            ctx.fillStyle = '#ef4444'; ctx.fillRect(-b.h / 2 + 2, -3, 8, 2);
            ctx.fillStyle = '#ffffff'; ctx.fillRect(-b.h / 2 + 2, -1, 8, 2);
            ctx.fillStyle = '#2563eb'; ctx.fillRect(-b.h / 2 + 2, 1, 8, 2);

            // Scheepsnaam
            ctx.fillStyle = '#0f172a';
            ctx.font = 'bold 8px sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('DE KROKANTE ZWAAN', 0, 14);
          }

          ctx.restore();
        }
        ctx.restore();
      }
    }

    // 2. Dubbele Hollandse Ophaalbrug "De Eendracht" (Hoofdstraat over de Prinsengracht)
    // De 2 brughelften kantelen omhoog naar de weg aan hun kant en vormen twee spectaculaire schansen!
    if (this.map.drawBridges) {
      for (const br of this.map.drawBridges) {
        ctx.save();

        const prog = br.openProgress || 0.0;
        const isLifted = prog > 0.02;
        const halfSpan = br.deckW / 2; // 60px voor elke brughelft (West & Oost)

        // A. Fysieke tussenruimte en verkorting bij het openen van de 2 brugvallen (Westval & Oostval)
        // De Westval zit vast aan de Westelijke weg (X=2420) en loopt schuin omhoog naar het Oosten
        // De Oostval zit vast aan de Oostelijke weg (X=2540) en loopt schuin omhoog naar het Westen
        const leafWidth = Math.max(10, halfSpan * (1 - prog * 0.78));
        const deckLift = prog * 42; // 3D-verheffing van de opengaande uiteinden

        const westHingeX = br.deckX;
        const westTipX = br.deckX + leafWidth;
        const eastHingeX = br.deckX + br.deckW;
        const eastTipX = br.deckX + br.deckW - leafWidth;
        const gapWidth = eastTipX - westTipX;

        // B. Schaduwen van de geheven brugdekken op de kademuren en het water
        if (isLifted) {
          ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
          // Schaduw Westelijke brugval (schans)
          ctx.fillRect(westHingeX - 4, br.deckY + 6, leafWidth + 12, br.deckH - 12);
          // Schaduw Oostelijke brugval (schans)
          ctx.fillRect(eastTipX - 8, br.deckY + 6, leafWidth + 12, br.deckH - 12);
        }

        // C. RENDER WESTELIJKE SCHANS (West Leaf Ramp - vast aan Westelijke weg X=2420)
        // 1. Onderstel & stalen draagbalken (zichtbaar door schuine hellingshoek)
        if (isLifted) {
          ctx.fillStyle = '#1e293b';
          ctx.fillRect(westHingeX, br.deckY, leafWidth + 4, br.deckH);
          ctx.strokeStyle = '#0f172a';
          ctx.lineWidth = 2;
          for (let ry = br.deckY + 16; ry < br.deckY + br.deckH; ry += 24) {
            ctx.beginPath();
            ctx.moveTo(westHingeX, ry);
            ctx.lineTo(westTipX, ry);
            ctx.stroke();
          }
        }

        // 2. Bovenkant wegdek met 3D-hellingverloop (Schans vanaf X=2420 omhoog naar het Oosten)
        const westGrad = ctx.createLinearGradient(westHingeX, br.deckY, westTipX, br.deckY);
        westGrad.addColorStop(0, '#1e293b'); // Laag op wegniveau
        westGrad.addColorStop(1, '#475569'); // Hoogste punt van de schans
        ctx.fillStyle = westGrad;
        ctx.fillRect(westHingeX, br.deckY, leafWidth, br.deckH);

        // Witte wegmarkering op Westelijke brugval
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2.5;
        ctx.setLineDash([14, 10]);
        ctx.beginPath();
        ctx.moveTo(westHingeX, br.deckY + br.deckH / 2);
        ctx.lineTo(westTipX - 4, br.deckY + br.deckH / 2);
        ctx.stroke();
        ctx.setLineDash([]);

        // Stunt Chevrons op de Westelijke Schans (wijzen naar rechts/oost over de gracht >>>)
        if (isLifted && leafWidth > 18) {
          ctx.fillStyle = '#facc15';
          ctx.font = 'bold 12px monospace';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('▶▶▶', westHingeX + leafWidth * 0.5, br.deckY + br.deckH * 0.3);
          ctx.fillText('▶▶▶', westHingeX + leafWidth * 0.5, br.deckY + br.deckH * 0.7);
        }

        // 3. Geel/Zwart gestreepte waarschuwingsrand op de kopse kant van de Westelijke schans
        if (isLifted) {
          const edgeW = 6;
          const stripeH = 10;
          for (let sy = br.deckY; sy < br.deckY + br.deckH; sy += stripeH) {
            ctx.fillStyle = (Math.floor(sy / stripeH) % 2 === 0) ? '#eab308' : '#0f172a';
            ctx.fillRect(westTipX - edgeW, sy, edgeW, Math.min(stripeH, br.deckY + br.deckH - sy));
          }
        }

        // D. RENDER OOSTELIJKE SCHANS (East Leaf Ramp - vast aan Oostelijke weg X=2540)
        // 1. Onderstel & stalen draagbalken
        if (isLifted) {
          ctx.fillStyle = '#1e293b';
          ctx.fillRect(eastTipX - 4, br.deckY, leafWidth + 4, br.deckH);
          ctx.strokeStyle = '#0f172a';
          ctx.lineWidth = 2;
          for (let ry = br.deckY + 16; ry < br.deckY + br.deckH; ry += 24) {
            ctx.beginPath();
            ctx.moveTo(eastTipX, ry);
            ctx.lineTo(eastHingeX, ry);
            ctx.stroke();
          }
        }

        // 2. Bovenkant wegdek met 3D-hellingverloop (Schans vanaf X=2540 omhoog naar het Westen)
        const eastGrad = ctx.createLinearGradient(eastHingeX, br.deckY, eastTipX, br.deckY);
        eastGrad.addColorStop(0, '#1e293b'); // Laag op wegniveau bij de oostkade
        eastGrad.addColorStop(1, '#475569'); // Hoogste punt van de schans
        ctx.fillStyle = eastGrad;
        ctx.fillRect(eastTipX, br.deckY, leafWidth, br.deckH);

        // Witte wegmarkering op Oostelijke brugval
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2.5;
        ctx.setLineDash([14, 10]);
        ctx.beginPath();
        ctx.moveTo(eastTipX + 4, br.deckY + br.deckH / 2);
        ctx.lineTo(eastHingeX, br.deckY + br.deckH / 2);
        ctx.stroke();
        ctx.setLineDash([]);

        // Stunt Chevrons op de Oostelijke Schans (wijzen naar links/west over de gracht <<<)
        if (isLifted && leafWidth > 18) {
          ctx.fillStyle = '#facc15';
          ctx.font = 'bold 12px monospace';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('◀◀◀', eastTipX + leafWidth * 0.5, br.deckY + br.deckH * 0.3);
          ctx.fillText('◀◀◀', eastTipX + leafWidth * 0.5, br.deckY + br.deckH * 0.7);
        }

        // 3. Geel/Zwart gestreepte waarschuwingsrand op de kopse kant
        if (isLifted) {
          const edgeW = 6;
          const stripeH = 10;
          for (let sy = br.deckY; sy < br.deckY + br.deckH; sy += stripeH) {
            ctx.fillStyle = (Math.floor(sy / stripeH) % 2 === 0) ? '#eab308' : '#0f172a';
            ctx.fillRect(eastTipX, sy, edgeW, Math.min(stripeH, br.deckY + br.deckH - sy));
          }
        }

        // Sluitnaad in gesloten toestand
        if (!isLifted) {
          ctx.fillStyle = '#0f172a';
          ctx.fillRect(br.deckX + halfSpan - 1.5, br.deckY, 3, br.deckH);
        }

        // E. ZICHTBARE TUSSENRUIMTE (Open Water Kolk)
        // In geopende toestand is het water tussen de twee schansen glashelder te zien
        if (isLifted && gapWidth > 2) {
          // Diepte-accent van de kadespleet
          ctx.fillStyle = 'rgba(8, 145, 178, 0.25)';
          ctx.fillRect(westTipX, br.deckY, gapWidth, br.deckH);

          // Witte schuimkraag / golfbeweging in de geopende brugspleet
          const waveT = Date.now() * 0.003;
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.45)';
          ctx.lineWidth = 1.5;
          for (let gy = br.deckY + 20; gy < br.deckY + br.deckH; gy += 45) {
            const wOff = Math.sin(waveT + gy * 0.1) * 8;
            ctx.beginPath();
            ctx.moveTo(westTipX + 4, gy + wOff);
            ctx.quadraticCurveTo(westTipX + gapWidth / 2, gy - 6 + wOff, eastTipX - 4, gy + wOff);
            ctx.stroke();
          }
        }

        // F. Witte Houten Bovenbouw (Hollandse Ophaalbrug Torens & Balanspriemen)
        const towerX1 = br.deckX - 12;
        const towerX2 = br.deckX + br.deckW + 12;
        const towerY1 = br.deckY + 12;
        const towerY2 = br.deckY + br.deckH - 12;

        // Witte Brugportalen / Hameipoorten op de kades
        ctx.fillStyle = '#f8fafc';
        ctx.strokeStyle = '#475569';
        ctx.lineWidth = 2;
        [ { x: towerX1, y: towerY1 }, { x: towerX1, y: towerY2 },
          { x: towerX2, y: towerY1 }, { x: towerX2, y: towerY2 } ].forEach(pt => {
          ctx.fillRect(pt.x - 8, pt.y - 8, 16, 16);
          ctx.strokeRect(pt.x - 8, pt.y - 8, 16, 16);
          // Messing pen
          ctx.fillStyle = '#ca8a04';
          ctx.beginPath();
          ctx.arc(pt.x, pt.y, 3, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = '#f8fafc';
        });

        // Witte Balanspriemen (kantelen achterwaarts bij opening)
        const armLean = prog * 28;
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 6;
        ctx.beginPath();
        // Westelijke balanspriemen
        ctx.moveTo(towerX1, towerY1);
        ctx.lineTo(towerX1 - armLean + 38, towerY1 - deckLift * 0.7);
        ctx.moveTo(towerX1, towerY2);
        ctx.lineTo(towerX1 - armLean + 38, towerY2 - deckLift * 0.7);
        // Oostelijke balanspriemen
        ctx.moveTo(towerX2, towerY1);
        ctx.lineTo(towerX2 + armLean - 38, towerY1 - deckLift * 0.7);
        ctx.moveTo(towerX2, towerY2);
        ctx.lineTo(towerX2 + armLean - 38, towerY2 - deckLift * 0.7);
        ctx.stroke();

        // Stalen kettingen naar de geopende brugval-uiteinden
        ctx.strokeStyle = '#94a3b8';
        ctx.lineWidth = 2.2;
        ctx.beginPath();
        // Westelijke ketting
        ctx.moveTo(towerX1 - armLean + 38, towerY1 - deckLift * 0.7);
        ctx.lineTo(westTipX, br.deckY + 24);
        ctx.moveTo(towerX1 - armLean + 38, towerY2 - deckLift * 0.7);
        ctx.lineTo(westTipX, br.deckY + br.deckH - 24);
        // Oostelijke ketting
        ctx.moveTo(towerX2 + armLean - 38, towerY1 - deckLift * 0.7);
        ctx.lineTo(eastTipX, br.deckY + 24);
        ctx.moveTo(towerX2 + armLean - 38, towerY2 - deckLift * 0.7);
        ctx.lineTo(eastTipX, br.deckY + br.deckH - 24);
        ctx.stroke();

        // G. Knipperende Rood-Witte Bruglichten (Zonder slagbomen zodat je vrij kunt springen!)
        const lightPoles = [
          { x: br.deckX - 22, y: br.deckY + 22 },
          { x: br.deckX - 22, y: br.deckY + br.deckH - 22 },
          { x: br.deckX + br.deckW + 22, y: br.deckY + 22 },
          { x: br.deckX + br.deckW + 22, y: br.deckY + br.deckH - 22 }
        ];

        // Teken altijd de vaste brugsein-palen langs de kades
        for (const lp of lightPoles) {
          // Voetstuk / Paal
          ctx.fillStyle = '#334155';
          ctx.fillRect(lp.x - 3, lp.y - 12, 6, 24);
          // Zwart schild / armatuur
          ctx.fillStyle = '#0f172a';
          ctx.beginPath();
          ctx.roundRect(lp.x - 7, lp.y - 10, 14, 20, 3);
          ctx.fill();
          ctx.strokeStyle = '#64748b';
          ctx.lineWidth = 1;
          ctx.stroke();
        }

        if (br.barrierLowered || isLifted) {
          const isBlinkA = (Math.floor(Date.now() / 260) % 2 === 0);

          for (const lp of lightPoles) {
            // Bovenste lamp
            const topActive = isBlinkA;
            ctx.fillStyle = topActive ? '#ef4444' : '#450a0a';
            ctx.beginPath();
            ctx.arc(lp.x, lp.y - 5, 4.5, 0, Math.PI * 2);
            ctx.fill();

            // Onderste lamp
            const botActive = !isBlinkA;
            ctx.fillStyle = botActive ? '#ef4444' : '#450a0a';
            ctx.beginPath();
            ctx.arc(lp.x, lp.y + 5, 4.5, 0, Math.PI * 2);
            ctx.fill();

            // Zachte rode lichtgloed rondom de actieve lamp
            const activeY = topActive ? lp.y - 5 : lp.y + 5;
            const glow = ctx.createRadialGradient(lp.x, activeY, 2, lp.x, activeY, 18);
            glow.addColorStop(0, 'rgba(239, 68, 68, 0.45)');
            glow.addColorStop(1, 'rgba(239, 68, 68, 0.0)');
            ctx.fillStyle = glow;
            ctx.beginPath();
            ctx.arc(lp.x, activeY, 18, 0, Math.PI * 2);
            ctx.fill();
          }

          // Brugstatus HUD Badge met Stunt-uitnodiging
          ctx.fillStyle = 'rgba(15, 23, 42, 0.92)';
          ctx.beginPath();
          ctx.roundRect(br.deckX + br.deckW / 2 - 125, br.deckY - 42, 250, 26, 6);
          ctx.fill();
          ctx.strokeStyle = '#eab308';
          ctx.lineWidth = 1.5;
          ctx.stroke();

          ctx.fillStyle = '#fef08a';
          ctx.font = 'bold 10px "Plus Jakarta Sans", sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          const bridgeText = isLifted ? t('world.bridgeLifted') : t('world.bridgeLights');
          ctx.fillText(bridgeText, br.deckX + br.deckW / 2, br.deckY - 29);
        } else {
          // Ruststand als brug dicht is: gedimde rustlampen
          for (const lp of lightPoles) {
            ctx.fillStyle = '#450a0a';
            ctx.beginPath();
            ctx.arc(lp.x, lp.y - 5, 4.5, 0, Math.PI * 2);
            ctx.arc(lp.x, lp.y + 5, 4.5, 0, Math.PI * 2);
            ctx.fill();
          }
        }

        ctx.restore();
      }
    }
  }

  // Stadsdecoraties: Authentieke Bomen, Parkbankjes, Fietsenrekken & Bloembakken
  renderStreetDecorations(ctx) {
    if (!this.map) return;
    ctx.save();

    // 1. Klassieke Groen/Houten Parkbankjes
    if (this.map.benches) {
      for (const bench of this.map.benches) {
        ctx.save();
        ctx.translate(bench.x, bench.y);
        ctx.rotate(bench.angle || 0);

        // Schaduw
        ctx.fillStyle = 'rgba(0, 0, 0, 0.22)';
        ctx.fillRect(-bench.w / 2 + 2, -bench.h / 2 + 2, bench.w, bench.h);

        // Gietijzeren zijkanten
        ctx.fillStyle = '#14532d'; // Donkergroen staal
        ctx.fillRect(-bench.w / 2, -bench.h / 2, 4, bench.h);
        ctx.fillRect(bench.w / 2 - 4, -bench.h / 2, 4, bench.h);

        // Houten latten
        ctx.fillStyle = '#b45309'; // Warm teakhout
        ctx.fillRect(-bench.w / 2 + 4, -bench.h / 2 + 1, bench.w - 8, 3.5);
        ctx.fillRect(-bench.w / 2 + 4, -bench.h / 2 + 5.5, bench.w - 8, 3.5);

        ctx.restore();
      }
    }

    // 2. Hollandse Fietsenrekken met Kleurrijke Omafietsen
    if (this.map.bikeRacks) {
      for (const rack of this.map.bikeRacks) {
        ctx.save();
        ctx.translate(rack.x, rack.y);
        ctx.rotate(rack.angle || 0);

        // Grijs verzinkt stalen buizenrek
        const totalW = rack.count * 18;
        ctx.fillStyle = '#94a3b8';
        ctx.fillRect(-totalW / 2, -4, totalW, 8);
        ctx.strokeStyle = '#475569';
        ctx.lineWidth = 1.2;
        ctx.strokeRect(-totalW / 2, -4, totalW, 8);

        // Geparkeerde fietsen
        if (rack.bikes) {
          rack.bikes.forEach((bike, bIdx) => {
            const bx = -totalW / 2 + 9 + bIdx * 18;
            // Fietsframe
            ctx.fillStyle = bike.color;
            ctx.fillRect(bx - 1.5, -9, 3, 18);
            // Wielen
            ctx.fillStyle = '#18181b';
            ctx.fillRect(bx - 1, -12, 2, 4);
            ctx.fillRect(bx - 1, 8, 2, 4);
            // Stuur & Bel
            ctx.fillStyle = '#cbd5e1';
            ctx.fillRect(bx - 4, -9, 8, 2);
            // Fietskrat / Bakkersmandje
            if (bike.basket) {
              ctx.fillStyle = '#9a3412';
              ctx.fillRect(bx - 3, -13, 6, 4);
            }
          });
        }
        ctx.restore();
      }
    }

    // 3. Terracotta Bloempotten & Stadsplantenbakken
    if (this.map.planters) {
      for (const pl of this.map.planters) {
        ctx.save();
        if (pl.type === 'terracotta') {
          // Terracotta ronde pot
          ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
          ctx.beginPath();
          ctx.ellipse(pl.x + 2, pl.y + 2, 8, 5, 0, 0, Math.PI * 2);
          ctx.fill();

          ctx.fillStyle = '#c2410c';
          ctx.beginPath();
          ctx.arc(pl.x, pl.y, 7, 0, Math.PI * 2);
          ctx.fill();

          // Bloeiende plant (groen loof + tulpen/geraniums)
          ctx.fillStyle = '#15803d';
          ctx.beginPath();
          ctx.arc(pl.x, pl.y, 5, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = pl.flowerColor || '#ef4444';
          ctx.beginPath();
          ctx.arc(pl.x - 2, pl.y - 2, 2, 0, Math.PI * 2);
          ctx.arc(pl.x + 2, pl.y - 1, 2, 0, Math.PI * 2);
          ctx.arc(pl.x, pl.y + 2, 2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          // Rechthoekige houten bloembak
          const bw = pl.w || 22;
          const bh = pl.h || 8;
          ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
          ctx.fillRect(pl.x - bw / 2 + 2, pl.y - bh / 2 + 2, bw, bh);

          ctx.fillStyle = '#78350f';
          ctx.fillRect(pl.x - bw / 2, pl.y - bh / 2, bw, bh);

          ctx.fillStyle = '#16a34a';
          ctx.fillRect(pl.x - bw / 2 + 2, pl.y - bh / 2 + 1, bw - 4, bh - 2);

          ctx.fillStyle = pl.flowerColor || '#f59e0b';
          for (let fx = pl.x - bw / 2 + 4; fx < pl.x + bw / 2 - 2; fx += 5) {
            ctx.beginPath();
            ctx.arc(fx, pl.y, 1.8, 0, Math.PI * 2);
            ctx.fill();
          }
        }
        ctx.restore();
      }
    }

    // 4. Realistische Nederlandse Bomen (Eiken, Lindes & Treurwilgen met windruis)
    if (this.map.trees) {
      const windPhase = Date.now() * 0.0018;
      for (const tree of this.map.trees) {
        ctx.save();
        const swayX = Math.sin(windPhase + tree.x * 0.02) * 2.2;
        const swayY = Math.cos(windPhase + tree.y * 0.02) * 1.5;

        // Zachte grondschaduw
        ctx.fillStyle = tree.shadowColor || 'rgba(0, 0, 0, 0.2)';
        ctx.beginPath();
        ctx.ellipse(tree.x + 6, tree.y + 6, tree.r * 1.1, tree.r * 0.75, 0, 0, Math.PI * 2);
        ctx.fill();

        // Boomstam kern
        ctx.fillStyle = '#78350f';
        ctx.beginPath();
        ctx.arc(tree.x, tree.y, 6, 0, Math.PI * 2);
        ctx.fill();

        // Gelaagde bladerkroon (Onderlaag donkergroen, toplaag frisgroen met wind-deining)
        const crownR = tree.r || 26;
        ctx.fillStyle = tree.color || '#15803d';
        ctx.beginPath();
        ctx.arc(tree.x + swayX * 0.5, tree.y + swayY * 0.5, crownR, 0, Math.PI * 2);
        ctx.fill();

        // Bladtextuur accenten / lichte toefjes
        ctx.fillStyle = '#22c55e';
        ctx.beginPath();
        ctx.arc(tree.x - crownR * 0.3 + swayX, tree.y - crownR * 0.3 + swayY, crownR * 0.5, 0, Math.PI * 2);
        ctx.arc(tree.x + crownR * 0.25 + swayX, tree.y - crownR * 0.2 + swayY, crownR * 0.45, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();
      }
    }

    ctx.restore();
  }

  // Wielrennerspeloton & Bakfietsmoeders
}
